import React from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { FbAdSet, getAdSetInsights, getAdSetPlacements } from '@/services/facebook-ad-sets.service';
import { FbAd } from '@/services/facebook-ads.service';
import { formatMetaCurrency, formatMetaCount, formatMetaInsightsValue } from '@/utils/currency';
import { StatusPill, ActionsDropdown, InlineErrorAlert } from './SharedComponents';
import { AdRow, AdWithCreative } from './AdCard';

interface AdsetSectionProps {
  adSet: FbAdSet;
  ads?: AdWithCreative[];
  adsLoading?: boolean;
  currency?: string;
  error?: string;
  isLoading?: boolean;
  adErrors?: Record<string, string>;
  adLoadingIds?: Set<string>;
  onToggleStatus?: () => void;
  onArchive?: () => void;
  onDismissError?: () => void;
  onAdToggleStatus?: (ad: FbAd) => void;
  onAdArchive?: (ad: FbAd) => void;
  onAdDismissError?: (adId: string) => void;
}

const formatDate = (val?: string | null): string => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString();
  } catch {
    return '—';
  }
};

export const AdsetSection: React.FC<AdsetSectionProps> = ({
  adSet,
  ads,
  adsLoading,
  currency = '',
  error,
  isLoading,
  adErrors,
  adLoadingIds,
  onToggleStatus,
  onArchive,
  onDismissError,
  onAdToggleStatus,
  onAdArchive,
  onAdDismissError,
}) => {
  const nav = useNavigate();
  const status = adSet.effective_status ?? adSet.status;
  const ins = getAdSetInsights(adSet);
  const placements = getAdSetPlacements(adSet);
  const placementLabel = placements.isAutomatic
    ? 'Automatic'
    : [
        placements.facebook && 'Facebook',
        placements.instagram && 'Instagram',
        placements.messenger && 'Messenger',
        placements.audienceNetwork && 'Audience Network',
      ].filter(Boolean).join(' + ') || 'None';

  const goToAdSet = () => nav(`/facebook/ad-sets/${adSet.id}`);

  return (
    <div
      className={clsx(
        'rounded-xl border border-border bg-surface-alt p-4 transition-colors',
        error && 'border-danger/40 ring-1 ring-danger/20',
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-success/25 bg-success-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-success">
              Ad Set
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goToAdSet();
              }}
              className="truncate text-sm font-semibold text-text-primary hover:text-primary"
              title={adSet.name}
            >
              {adSet.name}
            </button>
            <StatusPill status={status} size="small" />
          </div>

          {/* Stats line */}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-text-muted">
            <span>
              Reach <span className="font-semibold text-text-primary">{formatMetaCount(ins?.reach)}</span>
            </span>
            <span className="text-border-strong">·</span>
            <span>
              Clicks <span className="font-semibold text-text-primary">{formatMetaCount(ins?.clicks)}</span>
            </span>
            <span className="text-border-strong">·</span>
            <span>
              Spend{' '}
              <span className="font-semibold text-primary">
                {formatMetaInsightsValue(ins?.spend, currency)}
              </span>
            </span>
            <span className="text-border-strong">·</span>
            <span>
              CPM{' '}
              <span className="font-semibold text-text-primary">
                {formatMetaInsightsValue(ins?.cpm, currency)}
              </span>
            </span>
            {adSet.optimization_goal && (
              <>
                <span className="text-border-strong">·</span>
                <span>
                  Goal{' '}
                  <span className="font-semibold text-text-primary">
                    {adSet.optimization_goal.replace(/_/g, ' ')}
                  </span>
                </span>
              </>
            )}
          </div>

          {/* Budget + dates line */}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-text-muted">
            {adSet.daily_budget ? (
              <span>
                Daily{' '}
                <span className="font-semibold text-text-primary">
                  {formatMetaCurrency(adSet.daily_budget, currency)}
                </span>
              </span>
            ) : adSet.lifetime_budget ? (
              <span>
                Lifetime{' '}
                <span className="font-semibold text-text-primary">
                  {formatMetaCurrency(adSet.lifetime_budget, currency)}
                </span>
              </span>
            ) : (
              <span>
                Budget <span className="font-semibold text-warning">CBO</span>
              </span>
            )}
            {adSet.start_time && (
              <>
                <span className="text-border-strong">·</span>
                <span>
                  Start <span className="font-semibold text-text-primary">{formatDate(adSet.start_time)}</span>
                </span>
              </>
            )}
            {adSet.end_time && (
              <>
                <span className="text-border-strong">·</span>
                <span>
                  End <span className="font-semibold text-text-primary">{formatDate(adSet.end_time)}</span>
                </span>
              </>
            )}
          </div>

          {/* Placements line — surfaces "where it actually runs" so users
              don't have to drill into the ad set detail page to find out. */}
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-text-muted">
            <span>Runs on</span>
            <span
              className="font-semibold text-text-primary"
              title={placements.isAutomatic ? 'Meta automatic placements — eligible on all surfaces' : 'Manual placement selection'}
            >
              {placementLabel}
            </span>
            {placements.isAutomatic && (
              <span className="rounded-full bg-info-soft px-1.5 py-px text-[9px] font-semibold text-info">
                Auto
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <ActionsDropdown
            entityType="adset"
            currentStatus={status}
            isLoading={isLoading}
            onView={goToAdSet}
            onToggleStatus={onToggleStatus}
            onArchive={onArchive}
          />
        </div>
      </div>

      {error && <InlineErrorAlert message={error} onDismiss={onDismissError} />}

      {/* Ad rail */}
      <div className="mt-3 border-t border-border pt-3">
        <AdRow
          ads={ads ?? []}
          adSetId={adSet.id}
          currency={currency}
          hasFacebook={placements.facebook}
          hasInstagram={placements.instagram}
          errors={adErrors}
          loadingIds={adLoadingIds}
          onToggleStatus={onAdToggleStatus}
          onArchive={onAdArchive}
          onDismissError={onAdDismissError}
          loading={adsLoading && !ads}
        />
      </div>
    </div>
  );
};

export default AdsetSection;
